import { MutableRefObject, useEffect, useRef } from "react";

export default function useEventListener<
  Element extends HTMLElement,
  ElementEventMap extends HTMLElementEventMap,
  E extends keyof ElementEventMap
>(
  eventName: E,
  handler: EventListener,
  element?: MutableRefObject<Element | undefined> | Element,
  options?: boolean | AddEventListenerOptions
): void;
export default function useEventListener<E extends keyof WindowEventMap>(
  eventName: E,
  handler: EventListener,
  element?: Window,
  options?: boolean | AddEventListenerOptions
): void;
export default function useEventListener<E extends keyof HTMLElementEventMap>(
  eventName: E,
  handler: EventListener,
  element?:
    | MutableRefObject<HTMLElement | undefined>
    | undefined
    | null
    | HTMLElement
    | Window,
  options?: boolean | AddEventListenerOptions
): void {
  const savedHandler = useRef<EventListener>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const actualElement =
      element && "current" in element ? element.current : element;
    const isSupported = actualElement && actualElement.addEventListener;

    if (!isSupported) {
      return;
    }

    // Defining this function is necessary for functionality, see README for reasoning and tests for behaviour
    const eventListener: EventListener = event => {
      if (!savedHandler.current) {
        throw new Error(
          `The handler argument passed to useEventListener is not defined. This cannot be called by the event listener.`
        );
      }

      return savedHandler.current(event);
    };

    actualElement.addEventListener(eventName, eventListener, options);

    return () => {
      actualElement?.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, element, options]);
}
